// src/components/superAdminNav.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu, X, ChevronDown, Building2, BadgeCheck, Shield, LogOut
} from "lucide-react";
import Logo from "../assets/logo.png";

function safeParse(json) { try { return JSON.parse(json); } catch { return null; } }

// Public routes in your app (add more if needed)
const PUBLIC_PATHS = ["/", "/superadmin/login"];

const DropdownMenu = ({ title, icon: Icon, index, openIndex, setOpenIndex, children }) => {
  const isOpen = openIndex === index;
  const toggle = () => setOpenIndex(isOpen ? null : index);
  return (
    <div>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between rounded-lg px-4 py-3 text-slate-100 hover:bg-slate-800/70 transition"
      >
        <span className="flex items-center gap-3">
          <Icon className="h-5 w-5" />
          <span className="font-medium">{title}</span>
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && <ul className="ml-3 mt-1 space-y-1 border-l border-slate-700 pl-3">{children}</ul>}
    </div>
  );
};

const SuperAdminNav = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [user, setUser] = useState(null); // { user_id, username, role }

  // Redirect if not authenticated and path is protected
  useEffect(() => {
    const stored = safeParse(localStorage.getItem("patrol_superadmin"));
    setUser(stored);

    const path = location.pathname.toLowerCase();
    const isPublic = PUBLIC_PATHS.some(p => p.toLowerCase() === path);

    if (!stored && !isPublic) {
      navigate(PUBLIC_PATHS[0], { replace: true });
    }
  }, [location.pathname, navigate]);

  // Cross-tab logout sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "patrol_superadmin" && !e.newValue) {
        navigate(PUBLIC_PATHS[0], { replace: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [navigate]);

  // Close drawer when route changes on mobile
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    try { localStorage.clear(); sessionStorage.clear(); }
    finally { setUser(null); navigate(PUBLIC_PATHS[0], { replace: true }); }
  };

  const isActive = (to) => location.pathname.toLowerCase().startsWith(to.toLowerCase());

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-40 flex items-center justify-between bg-slate-950 px-4 py-3 text-slate-100 shadow">
        <div className="flex items-center gap-3">
          <img src={Logo} alt="logo" className="h-8 w-8 rounded" />
          <span className="font-semibold">Super Admin</span>
        </div>
        <button
          onClick={() => setDrawerOpen(!drawerOpen)}
          className="rounded-lg p-2 hover:bg-slate-900"
          aria-label="Open menu"
        >
          {drawerOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity md:hidden ${drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={() => setDrawerOpen(false)}
      />
      {/* Mobile drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-slate-950 text-slate-200 border-r border-slate-900 md:hidden
                    transform transition-transform duration-200 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center gap-3 p-5 bg-slate-900/70">
            <img src={Logo} alt="logo" className="h-12 w-12 rounded" />
            <div className="leading-tight">
              <p className="text-slate-100 font-semibold">Patrol Management</p>
              <p className="text-slate-400 text-sm">Super Admin Panel</p>
            </div>
          </div>

          <div className="px-5 py-4 border-b border-slate-800">
            <p className="text-sm text-slate-400">Signed in as</p>
            <p className="text-slate-100">
              <b>{user?.username || "—"}</b>{" "}
              {user?.role ? <small className="text-slate-400">({user.role})</small> : null}
            </p>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
            <DropdownMenu title="Tenants" icon={Building2} index={0} openIndex={openDropdown} setOpenIndex={setOpenDropdown}>
              <li>
                <Link
                  to="/superadmin/tenant"
                  className={`block rounded-md px-3 py-2 text-sm hover:bg-slate-800/60 ${isActive("/superadmin/tenant") ? "bg-slate-800 text-slate-100" : "text-slate-300"}`}
                >
                  All Tenants
                </Link>
              </li>
            </DropdownMenu>

            <DropdownMenu title="Licensing" icon={BadgeCheck} index={1} openIndex={openDropdown} setOpenIndex={setOpenDropdown}>
              <li>
                <Link
                  to="/superAdmin/admin_Menu"
                  className={`block rounded-md px-3 py-2 text-sm hover:bg-slate-800/60 ${isActive("/superAdmin/admin_Menu") ? "bg-slate-800 text-slate-100" : "text-slate-300"}`}
                >
                  Licenses
                </Link>
              </li>
            </DropdownMenu>

            <DropdownMenu title="Super Admin" icon={Shield} index={2} openIndex={openDropdown} setOpenIndex={setOpenDropdown}>
              <li>
                <Link
                  to="/superAdmin/superAdminUsers_List"
                  className={`block rounded-md px-3 py-2 text-sm hover:bg-slate-800/60 ${isActive("/superAdmin/superAdminUsers_List") ? "bg-slate-800 text-slate-100" : "text-slate-300"}`}
                >
                  Register
                </Link>
              </li>
            </DropdownMenu>
          </nav>

          <div className="p-4 border-t border-slate-900 bg-slate-900/70">
            <button
              onClick={handleLogout}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-white hover:bg-rose-700 transition"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Desktop sidebar */}
      <aside className="hidden md:block md:w-1/4 md:shrink-0 md:bg-slate-950 md:text-slate-200 md:border-r md:border-slate-900">
        <div className="flex h-screen sticky top-0 flex-col">
          <div className="flex items-center gap-3 p-5 bg-slate-900/70">
            <img src={Logo} alt="logo" className="h-12 w-12 rounded" />
            <div className="leading-tight">
              <p className="text-slate-100 font-semibold">Patrol Management</p>
              <p className="text-slate-400 text-sm">Super Admin Panel</p>
            </div>
          </div>

          <div className="px-5 py-4 border-b border-slate-800">
            <p className="text-sm text-slate-400">Signed in as</p>
            <p className="text-slate-100">
              <b>{user?.username || "—"}</b>{" "}
              {user?.role ? <small className="text-slate-400">({user.role})</small> : null}
            </p>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
            <DropdownMenu title="Tenants" icon={Building2} index={0} openIndex={openDropdown} setOpenIndex={setOpenDropdown}>
              <li>
                <Link
                  to="/superadmin/tenant"
                  className={`block rounded-md px-3 py-2 text-sm hover:bg-slate-800/60 ${isActive("/superadmin/tenant") ? "bg-slate-800 text-slate-100" : "text-slate-300"}`}
                >
                  All Tenants
                </Link>
              </li>
            </DropdownMenu>

            <DropdownMenu title="Licensing" icon={BadgeCheck} index={1} openIndex={openDropdown} setOpenIndex={setOpenDropdown}>
              <li>
                <Link
                  to="/superAdmin/admin_Menu"
                  className={`block rounded-md px-3 py-2 text-sm hover:bg-slate-800/60 ${isActive("/superAdmin/admin_Menu") ? "bg-slate-800 text-slate-100" : "text-slate-300"}`}
                >
                  Licenses
                </Link>
              </li>
            </DropdownMenu>

            <DropdownMenu title="Super Admin" icon={Shield} index={2} openIndex={openDropdown} setOpenIndex={setOpenDropdown}>
              <li>
                <Link
                  to="/superAdmin/superAdminUsers_List"
                  className={`block rounded-md px-3 py-2 text-sm hover:bg-slate-800/60 ${isActive("/superAdmin/superAdminUsers_List") ? "bg-slate-800 text-slate-100" : "text-slate-300"}`}
                >
                  Register
                </Link>
              </li>
            </DropdownMenu>
          </nav>

          <div className="p-4 border-t border-slate-900 bg-slate-900/70">
            <button
              onClick={handleLogout}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-white hover:bg-rose-700 transition"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default SuperAdminNav;
